import React, { useEffect, useMemo, useState } from 'react'
import Button from 'src/components/base/Button'
import ExpandedTable from 'src/components/base/ExpandedTable'
import Input from 'src/components/base/Input'
import { Config, Product } from 'src/types'
import useExtractProductFromConfig from 'src/hooks/useExtractProductFromConfig'
import MultiSelectInput from 'src/components/base/MultiSelectInput'
import useDebounce from 'src/hooks/useDebounce'

import './TerminalProducts.scss'

export type TPropsTerminalProducts = {
  config: Config
  setConfig: React.Dispatch<React.SetStateAction<Config>>
}

type FilteredProductGroupMap = Record<
  string,
  Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[]
>

function TerminalProducts({ config, setConfig }: TPropsTerminalProducts) {
  const {
    productGroupMap,
    getQuayNameBasedOnProductNameAndGroup,
    getHolderNamesBasedOnProductNameAndGroup
  } = useExtractProductFromConfig(config)
  const [selectedProductGroup, setSelectedProductGroup] = useState<string[]>([])
  const [productQuery, setProductQuery] = useState('')
  const debouncedProductQuery = useDebounce(productQuery, 200)
  const productGroupOptions = Object.keys(productGroupMap)

  const filteredProductGroupMapBasedOnSelectedGroup = useMemo(() => {
    if (selectedProductGroup.length === 0) {
      return productGroupMap
    }
    const result: FilteredProductGroupMap = {}
    selectedProductGroup.forEach(key => {
      result[key] = productGroupMap[key]
    })

    return result
  }, [productGroupMap, selectedProductGroup])

  const filteredProductGroupMap = useMemo(() => {
    if (!debouncedProductQuery) {
      return filteredProductGroupMapBasedOnSelectedGroup
    }
    const result: FilteredProductGroupMap = {}

    Object.keys(filteredProductGroupMapBasedOnSelectedGroup).forEach(key => {
      result[key] = filteredProductGroupMapBasedOnSelectedGroup[key].filter(product => {
        return product.product.toLowerCase().includes(debouncedProductQuery.toLowerCase())
      })
    })

    Object.keys(result).forEach(key => {
      if (result[key].length === 0) {
        delete result[key]
      }
    })

    return result
  }, [filteredProductGroupMapBasedOnSelectedGroup, debouncedProductQuery])

  const groups = useMemo(() => {
    return Object.keys(filteredProductGroupMap)
  }, [filteredProductGroupMap])

  return (
    <div className="terminal-products">
      <div className="topbar">
        <div className="topbar-section">
          <div className="topbar-title">
            <h2>Terminal Products Overview</h2>
            <span className="subtitle">Products can be added and updated as part of the ‘Quay Configuration’</span>
          </div>
        </div>
      </div>
      <div className="content-actions align-bottom">
        <div className="content-actions-group">
          <Input
            label="Search product"
            type="text"
            placeholder="Search product"
            onClick={e => e.stopPropagation()}
            onChange={e => setProductQuery(e.target.value)}
            value={productQuery}
          />
          {productQuery && (
            <Button
              variant="neutral-primary"
              icon="fa fa-times"
              onClick={() => setProductQuery('')}
              style={{ height: '48px', width: '48px' }}
            />
          )}
        </div>
        <MultiSelectInput
          label="Filter by product group"
          options={productGroupOptions}
          selectedOptions={selectedProductGroup}
          onSelectedOptionsChange={newSelectedOptions => setSelectedProductGroup(newSelectedOptions)}
          onClick={e => e.stopPropagation()}
          useAllSelectedLabel
        />
      </div>
      <div className="content">
        {groups.map(key => {
          const products = filteredProductGroupMap[key]
          return (
            <ExpandedTable key={key} emptyMessage={`No products found for ${key}`} empty={products.length === 0}>
              <ExpandedTable.Header>
                <ExpandedTable.Title as="div">{key}</ExpandedTable.Title>
              </ExpandedTable.Header>
              <ExpandedTable.Body>
                <ExpandedTable.Row>
                  <ExpandedTable.Cell asHeader style={{ width: '15%' }}>
                    Product
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell asHeader style={{ width: '15%' }}>
                    Unit
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell asHeader style={{ width: '15%' }}>
                    Quay
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell asHeader style={{ width: '15%' }}>
                    Jetty/Cargo belts
                  </ExpandedTable.Cell>
                  <ExpandedTable.Cell asHeader style={{ width: '40%' }} />
                </ExpandedTable.Row>
                {products.map((product, index) => {
                  return (
                    <ExpandedTable.Row key={index}>
                      <ExpandedTable.Cell style={{ width: '15%' }}>{product.product}</ExpandedTable.Cell>
                      <ExpandedTable.Cell style={{ width: '15%' }}>{product.productUnit}</ExpandedTable.Cell>
                      <ExpandedTable.Cell style={{ width: '15%' }}>
                        {getQuayNameBasedOnProductNameAndGroup(product.product, product.productGroup)}
                      </ExpandedTable.Cell>
                      <ExpandedTable.Cell style={{ width: '15%' }}>
                        {getHolderNamesBasedOnProductNameAndGroup(
                          product.product,
                          product.productGroup,
                          product.productUnit
                        ).map((holder, index) => {
                          return (
                            <div key={index} style={{ margin: '4px 0' }}>
                              {holder.name} ({holder.type})
                            </div>
                          )
                        })}
                      </ExpandedTable.Cell>
                      <ExpandedTable.Cell className="align-right" style={{ width: '40%' }} />
                    </ExpandedTable.Row>
                  )
                })}
              </ExpandedTable.Body>
            </ExpandedTable>
          )
        })}
      </div>
    </div>
  )
}

export default TerminalProducts
